import { useState } from "react";
import { Icon } from "../Icon";
import { fillText, clockText } from "../../utils/leadDisplay";

/* The wallet pop-ups: the same bottom sheet as the call confirmation on the list - an
   icon, the question in the reader's language, and two answers as big as the question.
   Every amount shown is the one the server sent. */

const TONES = {
  green: { bg: "#F1F7E3", fg: "#5E8700" },
  red: { bg: "#FEF2F2", fg: "#B91C1C" },
  amber: { bg: "#FFFBEB", fg: "#B45309" },
  slate: { bg: "#F1F5F9", fg: "#475569" },
};

const rupees = (n) => Math.round(Number(n) || 0).toLocaleString("en-IN");

function Sheet({ icon, tone = "green", title, lines = [], children, primary, secondary, onBackdrop }) {
  const c = TONES[tone] || TONES.green;
  return (
    <div className="fixed inset-0 z-[130] flex items-end justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label={secondary?.label || primary?.label} onClick={onBackdrop}
        className="absolute inset-0 w-full h-full bg-[rgba(15,23,42,0.45)] cursor-default" />
      <div className="relative w-full max-w-lg bg-white rounded-t-[22px] px-4 pt-3 shadow-[0_-8px_30px_rgba(0,0,0,0.18)]"
        style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}>
        <div className="w-10 h-1 rounded-full bg-[#E2E8F0] mx-auto mb-3" />
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: c.bg, color: c.fg }}>
            <Icon name={icon} size={20} strokeWidth={2} />
          </span>
          <div className="min-w-0 pt-0.5">
            <p className="text-[14px] font-extrabold text-[#0F172A] leading-snug">{title}</p>
            {lines.filter(Boolean).map((line, i) => (
              <p key={i} className="text-[11px] text-[#475569] leading-snug mt-1">{line}</p>
            ))}
          </div>
        </div>
        {children}
        <div className="flex gap-2 mt-4">
          {secondary && (
            <button type="button" onClick={secondary.onClick}
              className="flex-1 min-h-[48px] rounded-xl border border-[#CBD5E1] bg-white text-[12px] font-bold text-[#334155] active:scale-[0.98]">
              {secondary.label}
            </button>
          )}
          {primary && (
            <button type="button" onClick={primary.onClick} disabled={primary.busy}
              className="flex-[2] min-h-[48px] rounded-xl bg-[#7BAE00] text-white text-[12px] font-bold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] disabled:opacity-70">
              {primary.icon && <Icon name={primary.icon} size={16} stroke="#FFFFFF" fill={primary.icon === "phone" ? "#FFFFFF" : "none"} strokeWidth={1.6} />}
              {primary.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* The rule in the reader's words: "2 free calls every 6 hours keep bookings genuine." */
const ruleLine = (t, free, hours) => fillText(t("wg_extraText"), { n: free || 2, h: hours || 6 });
const freeAfter = (t, lang, resetsAt) => (resetsAt ? fillText(t("wg_freeAfter"), { time: clockText(new Date(resetsAt), lang) }) : t("hm_cancel"));

/** Free calls used and not enough balance: pay (₹29 first), then the call goes ahead - or wait for the free ones. */
export function LowBalanceSheet({ t, lang, fee, balance, resetsAt, free, hours, busy, onAdd, onCancel }) {
  const amounts = [Math.ceil(fee), 100, 200].filter((a, i, all) => a >= fee && all.indexOf(a) === i);
  const choices = amounts.length ? amounts : [Math.ceil(fee)];
  const [amount, setAmount] = useState(choices[0]);
  return (
    <Sheet icon="phone" tone="green"
      title={fillText(t("wg_extraTitle"), { fee: rupees(fee) })}
      lines={[ruleLine(t, free, hours), balance > 0 ? fillText(t("wg_balance"), { bal: rupees(balance) }) : null]}
      primary={{ label: busy ? t("walletProcessing") : fillText(t("wg_payUpi"), { amt: rupees(amount) }), onClick: () => onAdd(amount), busy }}
      secondary={{ label: freeAfter(t, lang, resetsAt), onClick: onCancel }}
      onBackdrop={busy ? undefined : onCancel}>
      <p className="text-[10.5px] font-bold text-[#334155] mt-3 mb-1.5">{t("wg_pick")}</p>
      <div className="flex gap-2">
        {choices.map((a) => (
          <button key={a} type="button" onClick={() => setAmount(a)} aria-pressed={amount === a}
            className={`flex-1 min-h-[42px] rounded-xl text-[12px] font-bold border-2 ${amount === a ? "bg-[#F6FAEC] text-[#5E8700] border-[#7BAE00]" : "bg-white text-[#475569] border-[#E5E7EB]"}`}>
            ₹{rupees(a)}
          </button>
        ))}
      </div>
    </Sheet>
  );
}

/** Enough balance: ₹29 from the wallet, said before the call is placed - or wait for the free ones. */
export function PaidCallSheet({ t, lang, fee, balance, resetsAt, free, hours, onCall, onCancel }) {
  return (
    <Sheet icon="phone" tone="green"
      title={fillText(t("wg_extraTitle"), { fee: rupees(fee) })}
      lines={[`${ruleLine(t, free, hours)} ${t("wg_extraTalk")}`, fillText(t("wg_balanceAfter"), { bal: rupees(balance), after: rupees(balance - fee) })]}
      primary={{ label: fillText(t("wg_payWallet"), { fee: rupees(fee) }), onClick: onCall, icon: "phone" }}
      secondary={{ label: freeAfter(t, lang, resetsAt), onClick: onCancel }}
      onBackdrop={onCancel} />
  );
}

/** The 2nd free call: it is the last one until the window closes. */
export function LastFreeSheet({ t, lang, free, resetsAt, onCall, onCancel }) {
  const time = resetsAt ? clockText(new Date(resetsAt), lang) : "";
  return (
    <Sheet icon="phone" tone="amber"
      title={fillText(t("wg_lastTitle"), { n: free || 2 })}
      lines={[time ? fillText(t("wg_lastText"), { n: free || 2, time }) : null]}
      primary={{ label: t("wg_call"), onClick: onCall, icon: "phone" }}
      secondary={{ label: t("hm_cancel"), onClick: onCancel }}
      onBackdrop={onCancel} />
  );
}

/** A call that could not start. */
export function CallFailedSheet({ t, onClose }) {
  return <Sheet icon="alertTri" tone="red" title={t("hm_callFailed")} primary={{ label: t("wg_done"), onClick: onClose }} onBackdrop={onClose} />;
}

/** A driver tapping Call on their own car or profile: no lead for yourself. */
export function OwnTargetSheet({ t, driver, onClose }) {
  return (
    <Sheet icon="info" tone="slate" title={t(driver ? "wg_ownProfile" : "wg_ownTitle")} lines={[t("wg_ownText")]}
      primary={{ label: t("wg_done"), onClick: onClose }} onBackdrop={onClose} />
  );
}

/** What happened to a payment. successLabel is "Call now" when a call is waiting. */
export function TopUpResultSheet({ t, result, successLabel, onSuccess, onRetry, onClose }) {
  const close = { label: t("hm_close"), onClick: onClose };
  switch (result?.status) {
    case "success":
      return (
        <Sheet icon="checkCircle" tone="green"
          title={fillText(t("wg_okTitle"), { amt: rupees(result.amount) })}
          lines={[fillText(t("wg_okText"), { bal: rupees(result.balance) })]}
          primary={{ label: successLabel || t("wg_done"), onClick: onSuccess || onClose, icon: successLabel ? "phone" : null }}
          secondary={successLabel ? close : null}
          onBackdrop={onClose} />
      );
    case "failed":
      return (
        <Sheet icon="alertTri" tone="red" title={t("wg_failTitle")} lines={[t("wg_failText")]}
          primary={{ label: t("wg_tryAgain"), onClick: onRetry }} secondary={close} onBackdrop={onClose} />
      );
    case "pending":
      return (
        <Sheet icon="clock" tone="amber" title={t("wg_pendingTitle")} lines={[t("wg_pendingText")]}
          primary={{ label: t("wg_done"), onClick: onClose }} onBackdrop={onClose} />
      );
    case "unavailable":
      return (
        <Sheet icon="info" tone="slate" title={t("wg_soonTitle")} lines={[t("wg_soonText")]}
          primary={{ label: t("wg_done"), onClick: onClose }} onBackdrop={onClose} />
      );
    default:
      return (
        <Sheet icon="alertTri" tone="red" title={t("wg_failTitle")} lines={[t("wg_startFail")]}
          primary={{ label: t("wg_tryAgain"), onClick: onRetry }} secondary={close} onBackdrop={onClose} />
      );
  }
}
